import { ExclamationTriangleIcon } from '@radix-ui/react-icons'
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { Check, Copy, EyeOff, Pencil } from 'lucide-react'
import { useContext, useMemo, useState } from 'react'

import BrokersMemberContext from 'components/context/BrokersMemberContext'
import CopyButtonWithAnimation from 'components/reusable/CopyButtonWithAnimation'
import DisplayTooltipOnHover from 'components/reusable/DisplayTooltipOnHover'
import { Badge } from 'slab'
import { formatSSN, getLocaleDateString } from 'utils'
import { useGetBrokersMembersMemberIdCoveredParticipants } from 'utils/web_api'
import { type BrokersCoveredParticipant } from 'utils/web_api.schemas'

import TanstackTable from '../../reusable/table/TanstackTable'

interface CoveredParticipantsTableProps {
  memberId: string
  planYear?: number
  onEdit: (participant: BrokersCoveredParticipant) => void
}

const columnHelper = createColumnHelper<BrokersCoveredParticipant>()

const CopyButton = ({ content }: { content: string }) => (
  <CopyButtonWithAnimation content={content}>
    <Copy className='h-4 w-4 text-gray-500' aria-label='Copy' />
  </CopyButtonWithAnimation>
)

const CopyableCell = ({ value }: { value?: string | number }) => {
  if (!value) {
    return null
  }

  const stringValue = typeof value === 'string' ? value : value.toString()

  return (
    <div className='flex items-center gap-2'>
      <span>{value}</span>
      <CopyButton content={stringValue} />
    </div>
  )
}

const createColumns = (onEdit: (participant: BrokersCoveredParticipant) => void) => [
  columnHelper.display({
    id: 'actions',
    header: 'Actions',
    cell: ({ row }) => (
      <button
        onClick={() => onEdit(row.original)}
        className='p-2 hover:bg-gray-100 rounded-full transition-colors'
        data-testid='edit-participant-button'
        aria-label='Edit participant'
      >
        <Pencil className='h-4 w-4 text-gray-500' />
      </button>
    ),
  }),
  columnHelper.accessor('given_name', {
    header: 'Given name',
    cell: ({ getValue }) => <CopyableCell value={getValue()} />,
  }),
  columnHelper.accessor('family_name', {
    header: 'Family name',
    cell: ({ getValue }) => <CopyableCell value={getValue()} />,
  }),
  columnHelper.display({
    id: 'source',
    header: 'Source',
    cell: ({ row }) => {
      const qleId = row.original.qualifying_life_event_id
      const qleStatus = row.original.qle_status
      if (qleId) {
        return (
          <DisplayTooltipOnHover
            text={
              qleStatus === 'in_progress'
                ? `QLE in progress: ${qleId.slice(-6)}`
                : `QLE added participant: ${qleId.slice(-6)}`
            }
          >
            <Badge
              type={qleStatus === 'in_progress' ? 'warning' : 'success'}
              leftIcon={
                qleStatus === 'in_progress' ? ExclamationTriangleIcon : Check
              }
            >
              QLE
            </Badge>
          </DisplayTooltipOnHover>
        )
      } else {
        return null
      }
    },
  }),
  columnHelper.accessor('relationship', {
    header: 'Relationship',
    cell: ({ getValue }) => getValue(),
  }),
  columnHelper.accessor('gender', {
    header: 'Gender',
    cell: ({ getValue }) => getValue(),
  }),
  columnHelper.accessor('dob', {
    header: 'Date of Birth',
    cell: ({ getValue }) => {
      const value = getValue()
      if (!value) return null

      const date = new Date(value)
      const formattedDate = getLocaleDateString(date)

      return <CopyableCell value={formattedDate} />
    },
  }),
  columnHelper.accessor('age', {
    header: 'Age',
    cell: ({ getValue }) => <CopyableCell value={getValue()} />,
  }),
  columnHelper.accessor('ssn', {
    header: 'SSN',
    cell: ({ getValue }) => {
      const value = getValue()
      if (!value) {
        return null
      }

      const [isVisible, setIsVisible] = useState(false)
      return (
        <div className='flex items-center gap-2'>
          {isVisible
            ? (
              <>
                <span>{formatSSN(value)}</span>
                <button
                  onClick={() => setIsVisible(false)}
                  className='p-1 hover:bg-gray-100 rounded-full transition-colors'
                  aria-label='Hide SSN'
                >
                  <EyeOff className='h-3 w-3 text-gray-500' />
                </button>
                <CopyButton content={value} />
              </>
              )
            : (
              <>
                <button
                  data-reveal-target='item'
                  onClick={() => setIsVisible(true)}
                  aria-label='Show SSN'
                  className='button-outline text-xs h-4'
                >
                  Show
                </button>
                <CopyButton content={value} />
              </>
              )}
        </div>
      )
    },
  }),
  columnHelper.accessor('tobacco', {
    header: 'Tobacco use',
    cell: ({ getValue }) => (getValue() ? 'Yes' : 'No'),
  }),
  columnHelper.accessor('status', {
    header: 'Status',
    cell: ({ getValue }) => (getValue() === 'pending' ? 'Pending' : 'Active'),
  }),
]

const CoveredParticipantsTable = ({
  memberId,
  planYear,
  onEdit,
}: CoveredParticipantsTableProps) => {
  const { isUserThatchAdmin = false } = useContext(BrokersMemberContext)

  // Memoize query parameters to prevent infinite re-renders
  const queryParams = useMemo(() => ({
    plan_year: planYear || 0,
    include_pending: true,
  }), [planYear])

  const { data, isLoading, error } =
    useGetBrokersMembersMemberIdCoveredParticipants(
      memberId,
      queryParams,
      {
        query: {
          enabled: !!memberId,
          refetchOnWindowFocus: false,
        },
      }
    )

  // Memoize participants data to prevent infinite re-renders
  const participants = useMemo(() => {
    return data || []
  }, [data])

  const columns = useMemo(() => createColumns(onEdit), [onEdit])

  const table = useReactTable({
    data: participants,
    columns,
    getCoreRowModel: getCoreRowModel(),
    state: {
      columnVisibility: {
        actions: isUserThatchAdmin,
      },
    },
  })

  return (
    <TanstackTable
      table={table}
      isLoading={isLoading}
      isError={!!error}
      noDataMessage='No covered participants found'
      tableMessageHeightClass='h-14'
      paginationLabelSingular='participant'
      paginationLabelPlural='participants'
      loadingSkeletonPageSizeOverride={2}
    />
  )
}

export default CoveredParticipantsTable
